<template>
  <div class="payment-widget flex flex-col lg:flex-row gap-6">
    <!-- Sol taraf: Ödeme Alanı -->
    <div class="payment-section flex-1 border border-gray-300 p-6 rounded-lg">
      <h2 class="text-xl font-semibold mb-4">Ödeme Bilgileri</h2>
      <form @submit.prevent="handlePayment" class="space-y-4">
        <div class="form-group">
          <label for="cardName" class="block text-sm font-medium"
            >Kart Üzerindeki İsim</label
          >
          <p v-if="validCardName === 2" class="text-red-500 text-sm">
            Geçersiz kart kullanicisi ismi
          </p>
          <input
            type="text"
            id="cardName"
            @blur="validateCardName"
            v-model="paymentDetails.cardName"
            class="input"
            required
          />
        </div>

        <div class="form-group">
          <label for="cardNumber" class="block text-sm font-medium"
            >Kart Numarası</label
          >
          <p v-if="validCardNumber === 2" class="text-red-500 text-sm">
            Geçersiz kart numarası
          </p>
          <input
            type="text"
            id="cardNumber"
            v-model="paymentDetails.cardNumber"
            @input="formatCardNumberInput"
            @blur="validateCardNumber"
            class="input"
            required
          />
        </div>

        <div class="form-group">
          <label for="expiryDate" class="block text-sm font-medium"
            >Son Kullanma Tarihi</label
          >
          <p v-if="validExpiryDate === 2" class="text-red-500 text-sm">
            Geçersiz son kullanma tarihi
          </p>
          <input
            type="text"
            id="expiryDate"
            placeholder="MM/YY"
            v-model="paymentDetails.expiryDate"
            @input="formatExpiryDateInput"
            @blur="validateExpiryDate"
            class="input"
            required
          />
        </div>

        <div class="form-group">
          <label for="cvc" class="block text-sm font-medium">CVC</label>
          <p v-if="validCVC === 2" class="text-red-500 text-sm">Geçersiz CVC</p>
          <input
            type="text"
            id="cvc"
            v-model="paymentDetails.cvc"
            @blur="validateCVC"
            class="input"
            required
          />
        </div>

        <button
          type="submit"
          :class="[
            paymentStatus === 1 ? 'bg-gray-400' : 'btn-primary',
            paymentStatus === 1 ? 'cursor-not-allowed' : 'cursor-pointer',
            'w-full mt-4',
          ]"
          :disabled="paymentStatus === 1"
        >
          {{ paymentStatus === 1 ? "Ödeme Yapılıyor..." : "Ödeme Yap" }}
        </button>
      </form>
    </div>

    <!-- Sağ taraf: Sipariş Özeti -->
    <div class="order-summary flex-1 border border-gray-300 p-6 rounded-lg">
      <h2 class="text-xl font-semibold mb-4">Sipariş Özeti</h2>
      <table class="w-full text-left border-collapse mb-4">
        <thead>
          <tr class="bg-gray-100">
            <th class="p-2 border-b">Ürün Adı</th>
            <th class="p-2 border-b">Birim Fiyat</th>
            <th class="p-2 border-b">Adet</th>
            <th class="p-2 border-b">Toplam</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in order.OrderItems" :key="item.PackageName">
            <td class="p-2 border-b">{{ item.PackageName }}</td>
            <td class="p-2 border-b">
              {{ (item.TotalPrice / item.Quantity).toFixed(2) }} TL
            </td>
            <td class="p-2 border-b">{{ item.Quantity }}</td>
            <td class="p-2 border-b">
              {{ item.TotalPrice.toFixed(2) }} TL
              <span v-if="isYearly">/ Yıllık</span>
              <span v-else>/ Aylık</span>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td colspan="3" class="p-2 border-t font-semibold">Genel Toplam</td>
            <td class="p-2 border-t font-semibold">
              {{ order.TotalAmount.toFixed(2) }} TL /
              {{ isYearly ? "Yıllık" : "Aylık" }}
            </td>
          </tr>
        </tfoot>
      </table>

      <h3 class="text-lg font-medium mb-2">Fatura Bilgileri</h3>
      <div v-if="order.InvoiceInformation">
        <p>
          <strong>Ad Soyad:</strong>
          {{ order.InvoiceInformation.FullName }}
        </p>
        <p><strong>Adres:</strong> {{ order.InvoiceInformation.Address }}</p>
        <p><strong>Email:</strong> {{ order.InvoiceInformation.Email }}</p>
      </div>
      <div v-else class="text-gray-500 italic">
        Fatura bilgileri yükleniyor...
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref } from "vue";
import { makePayment } from "@/services/paymentService";
import { Toast } from "@/services/toastService";
import {
  isValidCardName,
  formatCardNumber,
  isValidCardNumber,
  isValidExpiryDate,
  formatExpiryDate,
  isValidCVC,
} from "@/services/common";

const props = defineProps({
  order: {
    type: Object,
    required: true,
  },
  isYearly: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["payment-success", "payment-error"]);

const validCardNumber = ref(0);
const validExpiryDate = ref(0);
const validCVC = ref(0);
const paymentStatus = ref(0);
const validCardName = ref(0);

// Ödeme bilgileri için reaktif bir obje
const paymentDetails = ref({
  cardName: "",
  cardNumber: "",
  expiryDate: "",
  cvc: "",
});

const validateCardName = () => {
  validCardName.value = isValidCardName(paymentDetails.value.cardName) ? 1 : 2;
};

const validateCardNumber = () => {
  validCardNumber.value = isValidCardNumber(paymentDetails.value.cardNumber)
    ? 1
    : 2;
};

const validateExpiryDate = () => {
  validExpiryDate.value = isValidExpiryDate(paymentDetails.value.expiryDate)
    ? 1
    : 2;
};

const validateCVC = () => {
  validCVC.value = isValidCVC(paymentDetails.value.cvc) ? 1 : 2;
};

// Formatlama fonksiyonları
const formatCardNumberInput = () => {
  paymentDetails.value.cardNumber = formatCardNumber(
    paymentDetails.value.cardNumber
  );
};

const formatExpiryDateInput = () => {
  paymentDetails.value.expiryDate = formatExpiryDate(
    paymentDetails.value.expiryDate
  );
};

const handlePayment = async () => {
  paymentStatus.value = 1;

  try {
    const result = await makePayment(paymentDetails.value, props.order);

    if (result != null && result.Success) {
      paymentStatus.value = 0;
      emit("payment-success", result.Payload);
      Toast("Başarılı", "Ödeme başarıyla tamamlandı", "success");
    } else {
      paymentStatus.value = 0;
      emit("payment-error", result);
      Toast("Başarısız", "Ödeme başarısız \n Hata:" + result.Message, "error");
    }
  } catch (error) {
    paymentStatus.value = 0;
    emit("payment-error", error);
    Toast("Hata", "Ödeme sırasında bir hata oluştu", "error");
  }
};
</script>

<style scoped>
.payment-widget {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

@media (min-width: 1024px) {
  .payment-widget {
    flex-direction: row;
  }
}

.payment-section,
.order-summary {
  flex: 1;
}

.input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 0.375rem;
}

.btn-primary {
  padding: 0.75rem;
  background-color: #007bff;
  color: white;
  text-align: center;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
}

.btn-primary:hover {
  background-color: #0056b3;
}
</style>
